import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Package, Search, Download, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SECTION_NAME_MAP = {
  'fob': 'FOB Section', 'greige': 'Greige Section', 'greige-storage': 'Greige Storage',
  'goods-transfer': 'Goods Transfer', 'processing': 'Processing Section',
  'dispatch': 'Dispatch Section', 'inhouse': 'Inhouse Section', 'flexi': 'Flexi Section',
  'sample': 'Sample Section', 'tissue': 'Tissue Section', 'ecom': 'ECOM Section',
  'unassorted': 'Unassorted Section', 'sales-return': 'Sales Return', 'trims': 'Trims Section',
  'yarn': 'Yarn Section', 'shirt-manufacturing': 'Shirt Manufacturing',
  'accounts': 'Accounts Section', 'house-keeping': 'House Keeping',
  'electrical-goods': 'Electrical Goods', 'it-goods': 'IT Goods', 'r-and-m': 'R & M',
  'admin-section': 'Admin Section', 'weaving': 'Weaving Section',
};

const PAGE_LIMIT = 50;

export default function SectionInventoryReport() {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const sectionName = SECTION_NAME_MAP[sectionId] || sectionId;

  const [view, setView] = useState('usn');
  const [tab, setTab] = useState('detail');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalDesigns, setTotalDesigns] = useState(0);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [supported, setSupported] = useState(true);

  // Filters
  const [designFilter, setDesignFilter] = useState('');
  const [barcodeFilter, setBarcodeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const buildParams = useCallback((extra = {}) => {
    const params = new URLSearchParams({ section: sectionName, ...extra });
    if (designFilter) params.set('design', designFilter);
    if (barcodeFilter) params.set('barcode', barcodeFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return params.toString();
  }, [sectionName, designFilter, barcodeFilter, statusFilter, dateFrom, dateTo]);

  const fetchReport = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const qs = buildParams({ view, page: p, limit: PAGE_LIMIT });
      const res = await apiClient.get(`/section-inventory/report?${qs}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
      setSupported(res.data.supported !== false);
    } catch {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [buildParams, view]);

  const fetchSummary = useCallback(async () => {
    try {
      const qs = buildParams();
      const res = await apiClient.get(`/section-inventory/summary?${qs}`);
      setSummary(res.data.summary || []);
      setTotalCount(res.data.total_count || 0);
      setTotalDesigns(res.data.total_designs || 0);
      setSupported(res.data.supported !== false);
    } catch {
      // silent
    }
  }, [buildParams]);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await apiClient.get(`/section-inventory/statuses?section=${encodeURIComponent(sectionName)}`);
      setStatuses(res.data.statuses || []);
    } catch {
      // silent
    }
  }, [sectionName]);

  useEffect(() => { fetchStatuses(); }, [fetchStatuses]);
  useEffect(() => { fetchReport(0); fetchSummary(); }, [view, designFilter, barcodeFilter, statusFilter, dateFrom, dateTo]);

  const handleExport = async () => {
    try {
      const qs = buildParams({ view });
      const res = await apiClient.get(`/section-inventory/export?${qs}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory_${sectionId}_${view}_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const totalPages = Math.ceil(total / PAGE_LIMIT);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <BarChart3 className="w-5 h-5 text-emerald-400" />
          <div className="flex-1">
            <h1 className="text-lg font-bold">Inventory Report — {sectionName}</h1>
            <p className="text-xs text-[#E8DCC8]">{totalCount} items across {totalDesigns} designs</p>
          </div>
          <Button variant="outline" size="sm" className="text-[#F9FAFB] border-white/30 hover:bg-white/10" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-1" />Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-5">
        {!supported ? (
          <div className="text-center py-16 text-gray-400">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-lg font-medium">Inventory tracking not yet configured for {sectionName}</p>
            <p className="text-sm mt-1">This section will be supported in an upcoming update.</p>
          </div>
        ) : (
          <>
            {/* Filters Row */}
            <div className="flex flex-wrap items-end gap-3 mb-5 bg-white border border-[#E5DFD0] rounded-lg p-3">
              <div className="flex-1 min-w-[160px]">
                <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">Design Name</label>
                <Input placeholder="Filter by design..." value={designFilter} onChange={e => setDesignFilter(e.target.value)} className="h-8 text-sm" data-testid="filter-design" />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">Barcode</label>
                <Input placeholder="Filter by barcode..." value={barcodeFilter} onChange={e => setBarcodeFilter(e.target.value)} className="h-8 text-sm" data-testid="filter-barcode" />
              </div>
              <div className="w-[150px]">
                <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">Status</label>
                <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-sm" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[130px]">
                <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">From Date</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-sm" data-testid="filter-date-from" />
              </div>
              <div className="w-[130px]">
                <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">To Date</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-sm" data-testid="filter-date-to" />
              </div>
              <Button variant="ghost" size="sm" className="h-8 text-xs text-gray-500" onClick={() => { setDesignFilter(''); setBarcodeFilter(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); }} data-testid="clear-filters">
                Clear
              </Button>
            </div>

            {/* Tabs: Summary / Detail + View Toggle */}
            <div className="flex items-center justify-between mb-4">
              <Tabs value={tab} onValueChange={setTab} className="flex-1">
                <TabsList>
                  <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
                  <TabsTrigger value="detail" data-testid="tab-detail">Detail View</TabsTrigger>
                </TabsList>
              </Tabs>
              {tab === 'detail' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">View:</span>
                  <div className="flex border rounded-md overflow-hidden">
                    <button
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === 'usn' ? 'bg-[#2D5F4F] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setView('usn')}
                      data-testid="view-usn"
                    >USN</button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${view === 'bundle' ? 'bg-[#2D5F4F] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => setView('bundle')}
                      data-testid="view-bundle"
                    >Bundle</button>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Tab */}
            {tab === 'summary' && (
              <div className="space-y-2">
                {summary.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">No inventory data</p>
                ) : summary.map((s, idx) => (
                  <div key={idx} className="border border-[#E5DFD0] rounded-lg p-3 bg-white flex items-center justify-between hover:border-[#2D5F4F]/40 transition-colors cursor-pointer"
                    onClick={() => { setDesignFilter(s.design_name !== '-' ? s.design_name : ''); setBarcodeFilter(s.barcode !== '-' ? s.barcode : ''); setTab('detail'); }}
                    data-testid={`summary-row-${idx}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A1F2E] truncate">{s.design_name}</p>
                      <p className="text-xs text-gray-500 font-mono">{s.barcode}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex gap-1 flex-wrap justify-end">
                        {Object.entries(s.status_breakdown || {}).map(([st, cnt]) => (
                          <Badge key={st} variant="outline" className="text-[10px] font-mono">{st}: {cnt}</Badge>
                        ))}
                      </div>
                      <span className="text-lg font-bold text-[#2D5F4F] min-w-[50px] text-right">{s.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Detail Tab */}
            {tab === 'detail' && (
              <>
                {loading ? (
                  <p className="text-center text-gray-400 py-8">Loading...</p>
                ) : items.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">No items found</p>
                ) : (
                  <div className="border border-[#E5DFD0] rounded-lg overflow-hidden bg-white">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F5F1E8]">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">#</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">Code</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">Design</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">Barcode</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">Status</th>
                          {view === 'usn' && <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">Shade / Size</th>}
                          {view === 'usn' && <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">Bundle</th>}
                          {view === 'bundle' && <th className="text-center px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">USNs</th>}
                          <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">Date</th>
                          {view === 'usn' && <th className="text-left px-3 py-2 text-xs font-semibold text-[#1A1F2E] uppercase">Bin</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <tr key={idx} className="border-t border-[#E5DFD0]/50 hover:bg-[#FFFBF5]" data-testid={`item-row-${idx}`}>
                            <td className="px-3 py-2 text-xs text-gray-400">{page * PAGE_LIMIT + idx + 1}</td>
                            <td className="px-3 py-2 font-mono text-xs font-medium text-[#1A1F2E]">{item.code}</td>
                            <td className="px-3 py-2 text-xs text-[#1A1F2E] max-w-[180px] truncate">{item.design_name}</td>
                            <td className="px-3 py-2 text-xs font-mono text-gray-600">{item.barcode}</td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="text-[10px] capitalize">{item.status}</Badge>
                            </td>
                            {view === 'usn' && <td className="px-3 py-2 text-xs text-gray-500">{[item.shade, item.size].filter(Boolean).join(' / ') || '-'}</td>}
                            {view === 'usn' && <td className="px-3 py-2 text-xs font-mono text-gray-500">{item.bundle_code || '-'}</td>}
                            {view === 'bundle' && <td className="px-3 py-2 text-xs text-center font-medium">{item.usn_count}</td>}
                            <td className="px-3 py-2 text-xs text-gray-500">{typeof item.date === 'string' ? item.date.slice(0, 10) : '-'}</td>
                            {view === 'usn' && <td className="px-3 py-2 text-xs text-gray-500">{item.bin_code || '-'}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-xs text-gray-500">
                      Showing {page * PAGE_LIMIT + 1}-{Math.min((page + 1) * PAGE_LIMIT, total)} of {total}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page === 0} onClick={() => fetchReport(page - 1)} data-testid="prev-page">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-gray-600 px-2 py-1">Page {page + 1} / {totalPages}</span>
                      <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => fetchReport(page + 1)} data-testid="next-page">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
